import React, { useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { useSelector } from 'react-redux';
import { ToastManager } from '@casinofe/react-toast';
import arePropsEqual from 'react-fast-compare';
import { useFeatureIsOn } from '@growthbook/growthbook-react';
import SEOWrapper from '../../components/SEOWrapper/SEOWrapper';
import ToTopButton from '../../components/Button/ToTopButton/ToTopButton';
import PlayerLockModal from '../../components/PlayerLockModal/PlayerLockModal';
import NavigationModal from '../../components/Navigation/NavigationModal/NavigationModal';
import RecentsMigrationModal from '../../components/RecentsMigrationModal/RecentsMigrationModal';
import { SitewideSliderNavigationWidget } from '../../components/SitewideSliderNavigationWidget';
import GameRecommendationsModal from '../../components/GameRecommendationsModal/GameRecommendationsModalAsync';
import { useIsDesktopLayout } from '../../hooks/useIsDesktopLayout';
import { useChangeCashierResultURL } from '../../hooks/useChangeCashierResultURL';
import { isNativeSelector } from '../../redux/reducers/app/params';
import { useScrollbarWidth, useNavigationHeaderHeight } from './hooks';
import { useClickStreamPageView } from '../../hooks/useClickStreamPageView';
import { useIsBetbookMobileLayout } from '../../hooks/useMediaQuery';
import FEATURES from '../../constants/featureToggling';
import { useIsExternalNavbarEnabled } from '../../hooks/navbar';
import { useNativeHeader } from '../../hooks/useNativeHeader';
import { useMigrateRecentGames } from '../../hooks/useMigrateRecentGames';
import { isNavigationOpenSelector } from '../../redux/reducers/ui/modals';

import styles from './BaseLayout.module.css';

const BaseLayout = ({ header, sidebar, children, className }) => {
  useScrollbarWidth();
  useNativeHeader();
  useChangeCashierResultURL();
  useClickStreamPageView();
  useMigrateRecentGames();

  const ref = useNavigationHeaderHeight();
  const isDesktop = useIsDesktopLayout();

  const isBetbookMobile = useIsBetbookMobileLayout();
  const isNative = useSelector(isNativeSelector);

  const isNavigationOpen = useSelector(isNavigationOpenSelector);
  const isEnabledFixedHeader = useFeatureIsOn(FEATURES.ENABLE_FIXED_HEADER);
  const isGameRecommendationsModalEnabled = useFeatureIsOn(
    FEATURES.ENABLED_GAME_RECOMMENDATIONS_MODAL
  );
  const isStickySideMenu = useFeatureIsOn(FEATURES.ENABLED_STICKY_SIDE_MENU);
  const isFixed = isBetbookMobile && !isNative && isEnabledFixedHeader;
  const isExternalNavbarEnabled = useIsExternalNavbarEnabled();

  const isHeaderAvailable = useMemo(
    () => header && !isExternalNavbarEnabled,
    [header, isExternalNavbarEnabled]
  );

  return (
    <main ref={ref} className={classNames(styles.container, className)}>
      {!isDesktop && (
        <NavigationModal
          className={classNames(
            styles.navigation,
            isFixed && styles.fixed,
            isNavigationOpen && styles.navigationOpen
          )}
        />
      )}
      {isDesktop && sidebar && (
        <aside
          className={classNames(styles.sidebar, {
            [styles.sticky]: isStickySideMenu,
          })}
        >
          {sidebar}
        </aside>
      )}
      {isHeaderAvailable && (
        <header
          className={classNames(styles.header, {
            [styles.fixed]: isFixed,
            [styles.navigationOpen]: isNavigationOpen,
          })}
        >
          {header}
        </header>
      )}

      <section
        className={classNames(styles.content, {
          [styles.indent]: isHeaderAvailable,
          [styles.fixed]: isFixed,
        })}
      >
        <SitewideSliderNavigationWidget />

        <ToTopButton className={styles.button} />
        <RecentsMigrationModal />
        <PlayerLockModal />
        {isGameRecommendationsModalEnabled && <GameRecommendationsModal />}
        <ToastManager className={styles.toast} />

        <SEOWrapper>{children}</SEOWrapper>
      </section>
    </main>
  );
};

BaseLayout.propTypes = {
  header: PropTypes.oneOfType([PropTypes.element, PropTypes.node]),
  sidebar: PropTypes.oneOfType([PropTypes.element, PropTypes.node]),
};

export default React.memo(BaseLayout, arePropsEqual);
